import type { components as OpenAPI } from "@/autogen/openapi";
import URLS from "@/autogen/urls";
import type { Newsletter } from "@/types/newsletter";

export type SubscriptionState =
  | OpenAPI["schemas"]["SubscriberType"]
  | "not_subscribed"
  | "subscribed_elsewhere"
  | "premium"
  | "pending_validation"
  | "pending_subscription"
  | "invalid_email"
  | "invalid_email__regex"
  | "invalid_ip_address"
  | "invalid_subscription"
  | "errored_email"
  | "pending_email_address_confirmation"
  | "awaiting_email_address_confirmation";

export type SubscribeFormVariant =
  | "subscribe_page"
  | "archive_page"
  | "paywalled_archive_page";

// This should ideally match what's in `emails/middleware/utm.py`.
const RELEVANT_COOKIES = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

type AuthenticationStatus =
  | "unauthenticated"
  | "email_address_provided"
  | "email_address_confirmed"
  | "author_logged_in";

export type SubscriberFacingAuthentication = {
  status: AuthenticationStatus;
  email_address: string;
  subscribers: BasicSubscriber[];
};

const decomposeCookieString = (
  cookieString: string
): { [key: string]: string } => {
  return Object.fromEntries(
    cookieString
      .split("; ")
      .map((x) => x.split(/=(.*)$/, 2).map(decodeURIComponent))
  );
};

const filterObjectByKeys = (
  object: { [key: string]: string },
  keys: string[]
) => {
  return Object.fromEntries(
    Object.entries(object).filter(([key]) => keys.includes(key))
  );
};

const fetchRelevantCookies = (): { [key: string]: string } => {
  return filterObjectByKeys(
    decomposeCookieString(document.cookie),
    RELEVANT_COOKIES
  );
};

export type BasicSubscriber = {
  id: string;
  email_address: string;
  type: SubscriptionState;
  newsletter_id: string;
};

export type FormSurvey = {
  id: string;
  question: string;
  answers: string[];
  input_type: "radio" | "checkbox" | "text";
};

export const parseError = (
  error: unknown,
  data?: any
): { state: SubscriptionState; error: string } => {
  // Handle array-format validation errors from the API
  if (Array.isArray(error)) {
    const firstError = error[0] as
      | { loc?: string[]; msg?: string; type?: string }
      | undefined;
    if (firstError) {
      // Check if it's an email validation regex error
      if (
        firstError.type === "value_error.str.regex" &&
        firstError.loc?.includes("email_address")
      ) {
        return {
          state: "invalid_email__regex",
          error: "That email address is invalid",
        };
      }
      // Return a user-friendly message for other validation errors
      if (firstError.msg) {
        return { state: "errored_email", error: firstError.msg };
      }
    }
  }

  const errorString = typeof error === "string" ? error : String(error);

  if (errorString === "Email already exists") {
    return {
      state: "subscribed_elsewhere",
      error: "This email address is already subscribed.",
    };
  }
  const riskScores = data?.risk_scores;
  if (!riskScores) {
    return { state: "errored_email", error: errorString };
  }
  const ipRiskScore = Object.entries(riskScores)
    .filter(([key, value]) => key.includes("ip_address_"))
    .reduce((acc, [key, value]) => acc + (value as number), 0);
  // This needs to be as low as the AGGRESSIVE threshold goes.
  if (ipRiskScore >= 0.5) {
    return {
      state: "invalid_ip_address",
      error: "That IP address is problematic",
    };
  }
  return { state: "errored_email", error: errorString };
};

export const subscribe = async (
  emailAddress: string,
  metadata: { [key: string]: string },
  newsletter: Newsletter,
  referring_subscriber_id: string | null,
  turnstile_response_token: string | null
): Promise<
  | { error: string | object; subscriber?: BasicSubscriber; data?: any }
  | { subscriber: BasicSubscriber }
> => {
  const referralInformation = referring_subscriber_id
    ? { referring_subscriber_id: referring_subscriber_id }
    : {};

  const payload = {
    ...referralInformation,
    ...fetchRelevantCookies(),
    ...Object.fromEntries(new URLSearchParams(location.search)),
    metadata: Object.fromEntries(
      Object.entries(metadata).map(([key, value]) => [
        key,
        value === "on" ? "True" : value,
      ])
    ),
    tags: new URLSearchParams(document.location.search).getAll("tag"),
    email_address: emailAddress,
    newsletter_id: newsletter.id,
    turnstile_response_token: turnstile_response_token,
  };

  try {
    const response = await fetch(URLS["create-subscriber"](), {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
    const data = await response.json();
    if (data.detail || data.error) {
      return { error: data.detail || data.error, data: data };
    }
    const subscriber = {
      id: data.subscriber.id as string,
      email_address: data.subscriber.email_address as string,
      type: data.subscriber.type as OpenAPI["schemas"]["Subscriber"]["type"],
      newsletter_id: newsletter.id,
    };

    // Handle analytics callbacks. This could probably be better abstracted,
    // but it's not causing any angst at the moment.
    if (window.plausible) {
      window.plausible("Subscribe");
    } else if (window.fathom && newsletter.fathom_subscribe_code) {
      window.fathom.trackGoal(newsletter.fathom_subscribe_code, 0);
    }

    return { subscriber };
  } catch (e: any) {
    console.log(e);
    return {
      error: e.error,
      subscriber: e.subscriber,
      data: e.data,
    };
  }
};

export const mutableUrl = (
  newsletter: Newsletter,
  subscriber: { id: string } | undefined,
  route: string
): string | null => {
  if (!subscriber) {
    return null;
  }
  if (
    newsletter.domain !== "" &&
    newsletter.enabled_features.includes("domains")
  ) {
    return `/subscribers/${subscriber.id}${route}`;
  }

  return `/${newsletter.username}/subscribers/${subscriber.id}${route}`;
};

// Given: `https://buttondown.com/archive/foo-bar-baz`
// Return: `foo-bar-baz`
export const extractSlug = (url: string): string => {
  if (url.endsWith("/")) {
    return url.toString().split("/").reverse()[1];
  }
  return url.toString().split("/").reverse()[0];
};
